/* eslint-disable react/style-prop-object */
import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import cx from 'classnames';
import { fromCents, getCurrencyCode, getMinimumFractionDigits } from 'utils/helpers';
import Button from 'components/common/Button';
import AccordionTooltip from 'components/common/controls/AccordionTooltip';

const propTypes = {
  contract: PropTypes.object.isRequired,
  isUpgradeHighlighted: PropTypes.bool,
  isUpgradeSuccess: PropTypes.bool,
  setUpgradeHighlightOff: PropTypes.func,
  setUpgradeSuccessOff: PropTypes.func,
  isInDashboard: PropTypes.bool,
  isNativeAdvertisingEnabled: PropTypes.bool,
  isContractFrozen: PropTypes.bool,
  applyIncrease: PropTypes.func,
  isApplyIncreaseLoading: PropTypes.bool,
  isInCollection: PropTypes.bool,
};

const ContractStatus = (props) => {
  const {
    contract,
    isUpgradeSuccess,
    isUpgradeHighlighted,
    setUpgradeHighlightOff,
    isInDashboard,
    isContractFrozen,
    applyIncrease,
    isInCollection,
  } = props;
  const {
    availableLimit,
    creditApplication,
    upgrade,
    id,
    product,
    principalBalance = 0,
    productPrincipal,
  } = contract;
  const maxLimit = creditApplication.product.principal;
  const usedLimit = maxLimit - availableLimit;
  let items = [
    { id: 'availableLimit', value: availableLimit, mod: 'current' },
    { id: 'usedLimit', value: usedLimit, mod: 'used' },
  ];
  let used = (usedLimit / maxLimit) * 100;
  let isAUIL = false;
  let showUpgradeBanner = true;
  if (COUNTRY === 'AU') {
    isAUIL = product.type === 'INSTALLMENT';
    showUpgradeBanner = isInDashboard;
  }

  if (isAUIL) {
    items = [
      { id: 'principalBalance', value: contract.outstanding, mod: 'current' },
    ];
    used = ((productPrincipal - principalBalance) / productPrincipal) * 100;
  }

  const strokeWidth = isInDashboard ? '1.5' : '0.5';
  const viewBoxSize = isInDashboard ? 42 : 36;
  const coordinate = viewBoxSize / 2;
  const contractDashboardClassname = cx({
    'contract-dashboard': true,
    'contract-dashboard--type-vertical': !isInDashboard,
  });
  const upgradeAvailable = upgrade
    && upgrade.product
    && props.isNativeAdvertisingEnabled
    && !isContractFrozen
    && showUpgradeBanner;

  const upgradeAmount = upgradeAvailable && (
    <FormattedNumber
      style="currency"
      currency={getCurrencyCode()}
      value={fromCents(upgrade.product.principal)}
      minimumFractionDigits={getMinimumFractionDigits(
        fromCents(upgrade.product.principal),
        COUNTRY === 'AU' ? 0 : 2
      )}
      maximumFractionDigits={COUNTRY === 'AU' ? 0 : 2}
    />
  );

  const maxLimitAmount = (
    <FormattedNumber
      style="currency"
      currency={getCurrencyCode()}
      value={isAUIL ? fromCents(productPrincipal) || 0 : fromCents(maxLimit)}
      minimumFractionDigits={0}
      maximumFractionDigits={0}
    />
  );

  const contractDashboardContentClasses = cx({
    'contract-dashboard__content': true,
    'contract-dashboard__content--centered': isAUIL,
  });

  const upgradeContentClassname = cx({
    'upgrade-banner__content': true,
    'upgrade-banner__content--big-font': isAUIL,
  });

  const onIncreaseButtonClick = () => {
    applyIncrease(id, product.type, upgrade.increaseType);
  };

  return (
    <div className={contractDashboardClassname}>
      {!isInCollection && (
        <>
          <div className="contract-dashboard__chart">
            <svg
              width="100%"
              height="100%"
              viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
            >
              <circle
                cx={coordinate}
                cy={coordinate}
                r="15.91549430918954"
                fill="#fff"
              />
              <circle
                cx={coordinate}
                cy={coordinate}
                r="15.91549430918954"
                fill="transparent"
                stroke="#0082C6"
                strokeWidth={strokeWidth}
              />
              <circle
                cx={coordinate}
                cy={coordinate}
                r="15.91549430918954"
                fill="transparent"
                stroke="#E2E2E2"
                strokeWidth={strokeWidth}
                strokeDasharray={`${used} ${100 - used}`}
                strokeDashoffset="25"
              />
            </svg>
            <div className="contract-dashboard__chart-text">
              <span className="contract-dashboard__chart-text-value">
                {maxLimitAmount}
              </span>
              {isAUIL ? (
                <FormattedMessage id="selfservice.creditInformation.loanAmount" />
              ) : (
                <FormattedMessage id="selfservice.creditInformation.fullCredit" />
              )}
            </div>
          </div>
          <div className={contractDashboardContentClasses}>
            {items.map((item) => {
              const itemClassname = cx({
                'contract-dashboard__item': true,
                [`contract-dashboard__item--${item.mod}`]: item.mod,
                'contract-dashboard__item--centered': isAUIL,
              });
              const itemValue = fromCents(item.value);
              return (
                <span className={itemClassname} key={item.id}>
                  <FormattedMessage id={`selfservice.dashboard.${item.id}`} />
                  <span className="contract-dashboard__item-value">
                    <FormattedNumber
                      style="currency"
                      currency={getCurrencyCode()}
                      value={itemValue}
                      minimumFractionDigits={getMinimumFractionDigits(itemValue, 2)}
                      maximumFractionDigits={2}
                    />
                    {isAUIL && !isInDashboard && <span>*</span>}
                  </span>
                </span>
              );
            })}
          </div>
          {isAUIL && !isInDashboard && (
            <div className="contract-dashboard__item-hint">
              <AccordionTooltip
                title={
                  <FormattedMessage id="selfservice.dashboard.principalBalance.hint.title" />
                }
                hideIcon
                mod="centered"
              >
                <FormattedMessage id="selfservice.dashboard.principalBalance.hint" />
              </AccordionTooltip>
            </div>
          )}
          {!isUpgradeSuccess
            && upgradeAmount && (
            <div className="contract-dashboard__upgrade">
              {isUpgradeHighlighted ? (
                <span>
                  <span className="contract-dashboard__upgrade-title">
                    <FormattedMessage id="selfservice.upgrade.status.promo.title" />
                  </span>
                  <button
                    className="link"
                    onClick={setUpgradeHighlightOff}
                    type="button"
                  >
                    <FormattedMessage id="selfservice.upgrade.status.promo.close" />
                  </button>
                </span>
              ) : (
                <Button
                  isLoading={props.isApplyIncreaseLoading}
                  disabled={props.isApplyIncreaseLoading}
                  className="upgrade-banner"
                  onClick={onIncreaseButtonClick}
                >
                  <span className="upgrade-banner__text-holder">
                    <FormattedMessage id={`selfservice.upgrade.status.title.${isAUIL ? 'IL' : 'CL'}`} />
                    <br />
                    <span className={upgradeContentClassname}>
                      <FormattedMessage
                        id={`selfservice.upgrade.status.subtitle.${isAUIL ? 'IL' : 'CL'}`}
                        values={{ amount: upgradeAmount }}
                      />
                      <br />
                      <span className="upgrade-banner__terms">
                        <FormattedMessage id="selfservice.upgrade.status.terms" />
                      </span>
                    </span>
                  </span>

                  <div className="btn ripple-parent contract-dashboard__upgrade-btn">
                    <FormattedMessage
                      id="selfservice.upgrade.status.button"
                      values={{ amount: upgradeAmount }}
                    />
                  </div>
                </Button>
              )}
            </div>
          )}
          {isUpgradeSuccess && (
            <div className="contract-dashboard__upgrade contract-dashboard__upgrade--success">
              <div className="contract-dashboard__upgrade-success-icon">
                <span className="icon icon--done" />
              </div>
              <div className="contract-dashboard__upgrade-success-message">
                <FormattedMessage
                  id="selfservice.dashboard.upgradeSuccessText"
                  values={{ amount: maxLimitAmount }}
                />
              </div>
              <div className="contract-dashboard__upgrade-success-close">
                <button
                  type="button"
                  className="contract-dashboard__upgrade-success-close-btn"
                  onClick={props.setUpgradeSuccessOff}
                />
              </div>
            </div>
          )}
        </>
      )}
      {!isInDashboard
        && !isAUIL && (
          <ul className="contract-dashboard__links">
            <li className="contract-dashboard__links-item">
              <a
                href={`${API_HOST}/contracts/${id}/document`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FormattedMessage id="selfservice.creditinformation.downloadContractLink" />
              </a>
            </li>
          </ul>
      )}
    </div>
  );
};

ContractStatus.propTypes = propTypes;

export default ContractStatus;
